import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api, { authAPI } from '../services/api';
import { refreshSession } from '../shared/services/apiClient';
import { formatApiError } from '../utils/errorMessage';
import { clearSavedBusiness } from '../utils/businessSelection';
import { checkSession } from '../utils/sessionCheck';
import { savePendingTwoFactor } from '../utils/pendingTwoFactor';

const AuthContext = createContext();

const DEFAULT_IDLE_MINUTES = 60;
// The server's idle window in ms; the default when /auth/me did not send one.
export function idleTimeoutMs(minutes) {
  const n = Number(minutes);
  const valid = Number.isInteger(n) && n >= 5 && n <= 720 ? n : DEFAULT_IDLE_MINUTES;
  return valid * 60 * 1000;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cached = sessionStorage.getItem('user');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionEpoch = useRef(0);
  

  useEffect(() => {
    const epoch = sessionEpoch.current;
    let hadCachedUser = false;
    try { hadCachedUser = Boolean(sessionStorage.getItem('user')); } catch {}
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    checkSession({ getMe: () => api.get('/auth/me'), refresh: refreshSession })
      .then((outcome) => {
        if (sessionEpoch.current !== epoch) return;
        if (outcome.user) {
          setUser(prev => {
            const merged = { ...(prev || {}), ...outcome.user };
            try { sessionStorage.setItem('user', JSON.stringify(merged)); } catch {}
            return merged;
          });
          setToken('session');
        } else if (outcome.unreachable && hadCachedUser) {
          // The server did not answer (deploy / cold start). Stay signed in;
          // the next API call that gets a real 401 ends the session.
          setToken('session');
        } else {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refreshToken');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('formationDateEdits');
          setToken(null);
          setUser(null);
        }
        setLoading(false);
      });
  }, []);

  const acceptSession = (nextUser) => {
    sessionEpoch.current += 1;
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    setToken('session');
    setUser(nextUser);
    try { sessionStorage.setItem('user', JSON.stringify(nextUser)); } catch {}
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.data.requires2FA) {
        // No tokens yet - the account has 2FA enabled, caller must collect a
        // code and call verifyTwoFactor() with the returned pendingToken.
        sessionStorage.removeItem('formationDateEdits');
        return {
          success: false,
          requires2FA: true,
          pendingToken: response.data.pendingToken,
          methods: response.data.methods
        };
      }

      const { user } = response.data;

      sessionStorage.removeItem('formationDateEdits');
      acceptSession(user);

      return { success: true };
    } catch (error) {
      sessionStorage.removeItem('formationDateEdits');
      return {
        success: false,
        error: formatApiError(error, 'Login failed')
      };
    }
  };

  const verifyTwoFactor = async (pendingToken, code) => {
    try {
      const response = await api.post('/auth/2fa/verify', { pendingToken, code });
      const { user } = response.data;

      sessionStorage.removeItem('formationDateEdits');
      acceptSession(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiError(error, 'Verification failed')
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { user } = response.data;

      sessionStorage.removeItem('formationDateEdits');
      acceptSession(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiError(error, 'Registration failed')
      };
    }
  };

  const logout = useCallback(() => {
    if (logout.current) return;
    logout.current = true;
    const leave = () => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      clearSavedBusiness();
      sessionStorage.removeItem('formationDateEdits');
      setToken(null);
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
      window.location.assign('/');
    };
    // Leave only after the server clears the session cookie. Leaving first
    // reloads the app while the cookie is still valid and sends the person back in.
    api.post('/auth/logout').then(leave).catch((error) => {
      if (error.response && error.response.status === 401) leave();
      else logout.current = false;
    });
  }, []);

  // Sliding idle timeout (the server's SESSION_IDLE_MINUTES, 60 by default,
  // sent as user.session_policy by /auth/me). Activity in any tab counts: the
  // last activity time is shared through localStorage. While active, the
  // session is refreshed at most every 5 minutes, which is what keeps it alive
  // on the server (backend/utils/sessionPolicy.js); after the idle window this
  // tab signs out, and the server would refuse to renew the session anyway.
  const idleMinutes = Number(user && user.session_policy && user.session_policy.idle_minutes);
  useEffect(() => {
    if (!token) return;
    const IDLE_TIMEOUT_MS = idleTimeoutMs(idleMinutes);
    const KEEPALIVE_EVERY_MS = 5 * 60 * 1000;
    const CHECK_EVERY_MS = 30 * 1000;
    const ACTIVITY_KEY = 'elonest_last_activity';
    let lastKeepalive = Date.now();
    let lastWrite = 0;

    const lastActivity = () => {
      try {
        const stored = Number(localStorage.getItem(ACTIVITY_KEY));
        return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
      } catch {
        return Date.now();
      }
    };

    const onActivity = () => {
      const now = Date.now();
      // Coming back after the idle limit signs out instead of counting as activity.
      if (now - lastActivity() >= IDLE_TIMEOUT_MS) {
        logout();
        return;
      }
      if (now - lastWrite > 1000) {
        lastWrite = now;
        try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch {}
      }
      if (now - lastKeepalive > KEEPALIVE_EVERY_MS) {
        lastKeepalive = now;
        refreshSession().catch(() => {});
      }
    };

    const check = () => {
      if (Date.now() - lastActivity() >= IDLE_TIMEOUT_MS) logout();
    };

    // Coming back to the tab is activity too: it renews the session (and wakes
    // a sleeping API) before the first click, instead of on a failed request.
    const onVisible = () => {
      if (document.visibilityState === 'visible') onActivity();
    };

    // Reading and pointing count, not only clicks and typing (mousemove is
    // throttled by onActivity's own 1-second write guard).
    const activityEvents = ['mousedown', 'mousemove', 'pointerdown', 'wheel', 'keydown', 'scroll', 'touchstart', 'click', 'focus'];
    try { localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch {}
    activityEvents.forEach((event) => window.addEventListener(event, onActivity, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(check, CHECK_EVERY_MS);

    return () => {
      clearInterval(interval);
      activityEvents.forEach((event) => window.removeEventListener(event, onActivity, { capture: true, passive: true }));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, logout, idleMinutes]); // Re-run when token or the server policy changes

  const loginWithGoogle = async () => {
    try {
      const redirect = window.location.pathname || '/dashboard';
      const response = await authAPI.getGoogleAuthUrl(redirect);
      // Redirect to Google OAuth
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error initiating Google login:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      return {
        success: false,
        error: formatApiError(error, 'Failed to initiate Google login')
      };
    }
  };

  const handleGoogleCallback = async (code, state) => {
    try {
      const response = await authAPI.googleCallback(code, state);
      const { user, redirect } = response.data;
      if (response.data.requires2FA) {
        // Google proved the email; the app's second factor is still asked.
        savePendingTwoFactor({ pendingToken: response.data.pendingToken, methods: response.data.methods, redirect: redirect || '/dashboard' });
        return { success: false, requires2FA: true };
      }

      acceptSession(user);
      return { success: true, redirect: redirect || '/dashboard' };
    } catch (error) {
      try {
        const me = await api.get('/auth/me');
        if (me.data && me.data.email) {
          acceptSession(me.data);
          return { success: true, redirect: state || '/dashboard' };
        }
      } catch (ignored) {
        // The code was not accepted and no session was stored.
      }
      return {
        success: false,
        error: formatApiError(error, 'Google authentication failed')
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    verifyTwoFactor,
    register,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
